/**
 * ProtocolDetailModal - Modal for viewing, creating, and editing protocols.
 *
 * Three modes:
 * - **view**: Read-only view of a library protocol with "Save" and "Customize" buttons
 * - **edit**: Edit an existing user protocol with "Delete", "Cancel", "Save" buttons
 * - **create**: Create a new protocol with "Cancel", "Save Protocol" buttons
 *
 * This replaces ProtocolFormPopover for a better detail view experience.
 */

import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@heidi/design-system/dialog';
import { Input } from '@heidi/design-system/input';
import { Label } from '@heidi/design-system/label';
import { Textarea } from '@heidi/design-system/textarea';
import {
  TypographyCaption,
  TypographyP2,
  TypographyP2Bold,
} from '@heidi/design-system/typography';
import { memo, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { formFieldCharCountMessages } from './form-field-char-count.messages';
import { protocolDetailModalMessages } from './protocol-detail-modal.messages';
import {
  SHORTCUT_NAME_MAX_LENGTH,
  SHORTCUT_PROMPT_MAX_LENGTH,
} from './shortcut-constants';
import type { ResolvedShortcut } from './shortcut-marketplace-data';

export type ProtocolDetailMode = 'view' | 'edit' | 'create';

export interface ProtocolDetailModalProps {
  /** Modal mode - determines UI and available actions */
  mode: ProtocolDetailMode;
  /** Whether the modal is open */
  open: boolean;
  /** Callback when open state changes */
  onOpenChange: (open: boolean) => void;
  /** Protocol data (required for view/edit modes) */
  protocol?: ResolvedShortcut | { id: string; label: string; query: string };
  /** Called when saving (view mode: saves as-is, edit/create: saves with edits) */
  onSave: (label: string, query: string) => void;
  /** Called when user wants to customize a library protocol (view mode only) */
  onCustomize?: () => void;
  /** Called when deleting a protocol (edit mode only) */
  onDelete?: () => void;
}

export const ProtocolDetailModal = memo(
  ({
    mode,
    open,
    onOpenChange,
    protocol,
    onSave,
    onCustomize,
    onDelete,
  }: ProtocolDetailModalProps) => {
    const intl = useIntl();
    const [label, setLabel] = useState('');
    const [query, setQuery] = useState('');

    // Reset form when modal opens or protocol changes
    useEffect(() => {
      if (open && protocol) {
        setLabel(protocol.label);
        setQuery(protocol.query);
      } else if (open && mode === 'create') {
        setLabel('');
        setQuery('');
      }
    }, [open, protocol, mode]);

    const handleSave = () => {
      if (mode === 'view') {
        // In view mode, save the original protocol values
        onSave(protocol?.label ?? '', protocol?.query ?? '');
      } else {
        // In edit/create mode, save the edited values
        if (label.trim() && query.trim()) {
          onSave(label.trim(), query.trim());
        }
      }
      onOpenChange(false);
    };

    const handleCustomize = () => {
      onCustomize?.();
    };

    const handleDelete = () => {
      onDelete?.();
      onOpenChange(false);
    };

    const handleCancel = () => {
      onOpenChange(false);
    };

    const isQueryOverLimit = query.length > SHORTCUT_PROMPT_MAX_LENGTH;
    const isNameOverLimit = label.length > SHORTCUT_NAME_MAX_LENGTH;
    const isValid = label.trim() && query.trim() && !isQueryOverLimit && !isNameOverLimit;

    const dialogTitle = useMemo(() => {
      const titleLabel =
        mode === 'create'
          ? ''
          : protocol?.label?.trim()
            ? protocol.label
            : intl.formatMessage(protocolDetailModalMessages.protocolNameFallback);

      return intl.formatMessage(protocolDetailModalMessages.dialogTitle, {
        mode,
        label: titleLabel,
      });
    }, [intl, mode, protocol?.label]);

    const namePlaceholder = intl.formatMessage(
      protocolDetailModalMessages.namePlaceholder
    );
    const instructionsPlaceholder = intl.formatMessage(
      protocolDetailModalMessages.instructionsPlaceholder
    );
    const nameCharCount = intl.formatMessage(formFieldCharCountMessages.charCount, {
      current: label.length,
      max: SHORTCUT_NAME_MAX_LENGTH,
    });
    const queryCharCount = intl.formatMessage(formFieldCharCountMessages.charCount, {
      current: query.length,
      max: SHORTCUT_PROMPT_MAX_LENGTH,
    });

    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="gap-6 bg-surface-page p-6 shadow-lg sm:max-w-[520px] sm:rounded-xl">
          <DialogHeader className="gap-1">
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogDescription asChild>
              <TypographyCaption className="text-text-tertiary">
                <FormattedMessage
                  {...protocolDetailModalMessages.modalDescription}
                  values={{ mode }}
                />
              </TypographyCaption>
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-col gap-6">
            {/* Protocol Name */}
            <div className="flex flex-col gap-1.5">
              {mode === 'view' ? (
                <>
                  <TypographyP2Bold className="text-text-secondary">
                    <FormattedMessage {...protocolDetailModalMessages.protocolName} />
                  </TypographyP2Bold>
                  <TypographyP2 className="text-text-secondary">
                    {protocol?.label}
                  </TypographyP2>
                </>
              ) : (
                <>
                  <Label
                    htmlFor="protocol-name"
                    className="font-medium text-text-secondary"
                  >
                    <FormattedMessage {...protocolDetailModalMessages.protocolName} />
                  </Label>
                  <Input
                    id="protocol-name"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder={namePlaceholder}
                    autoFocus={mode === 'create'}
                  />
                  <TypographyCaption
                    className={cn(
                      'self-end',
                      isNameOverLimit
                        ? 'text-validation-destructive'
                        : 'text-text-tertiary'
                    )}
                  >
                    {nameCharCount}
                  </TypographyCaption>
                </>
              )}
            </div>

            {/* Protocol Instructions */}
            <div className="flex flex-col gap-1.5">
              {mode === 'view' ? (
                <>
                  <TypographyP2Bold className="text-text-secondary">
                    <FormattedMessage
                      {...protocolDetailModalMessages.protocolInstructions}
                    />
                  </TypographyP2Bold>
                  <TypographyP2 className="whitespace-pre-wrap text-text-secondary">
                    {protocol?.query}
                  </TypographyP2>
                </>
              ) : (
                <>
                  <Label
                    htmlFor="protocol-query"
                    className="font-medium text-text-secondary"
                  >
                    <FormattedMessage
                      {...protocolDetailModalMessages.protocolInstructions}
                    />
                  </Label>
                  <Textarea
                    id="protocol-query"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={instructionsPlaceholder}
                    className={cn(
                      'min-h-[120px] resize-none',
                      isQueryOverLimit && 'border-validation-destructive'
                    )}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.metaKey) {
                        e.preventDefault();
                        if (isValid) handleSave();
                      }
                    }}
                  />
                  <TypographyCaption
                    className={cn(
                      'self-end',
                      isQueryOverLimit
                        ? 'text-validation-destructive'
                        : 'text-text-tertiary'
                    )}
                  >
                    {queryCharCount}
                  </TypographyCaption>
                </>
              )}
            </div>
          </div>

          <DialogFooter className={cn(mode === 'edit' && onDelete && 'justify-between')}>
            {/* View mode actions */}
            {mode === 'view' && (
              <>
                {onCustomize && (
                  <Button size="lg" variant="ghost" onClick={handleCustomize}>
                    <FormattedMessage {...protocolDetailModalMessages.customise} />
                  </Button>
                )}
                <Button size="lg" onClick={handleSave}>
                  <FormattedMessage {...protocolDetailModalMessages.save} />
                </Button>
              </>
            )}

            {/* Edit mode actions */}
            {mode === 'edit' && (
              <>
                {onDelete && (
                  <Button size="lg" variant="destructive_soft" onClick={handleDelete}>
                    <FormattedMessage {...protocolDetailModalMessages.delete} />
                  </Button>
                )}
                <div className="flex gap-2">
                  <Button size="lg" variant="ghost" onClick={handleCancel}>
                    <FormattedMessage {...protocolDetailModalMessages.cancel} />
                  </Button>
                  <Button size="lg" onClick={handleSave} disabled={!isValid}>
                    <FormattedMessage {...protocolDetailModalMessages.save} />
                  </Button>
                </div>
              </>
            )}

            {/* Create mode actions */}
            {mode === 'create' && (
              <>
                <Button size="lg" variant="ghost" onClick={handleCancel}>
                  <FormattedMessage {...protocolDetailModalMessages.cancel} />
                </Button>
                <Button size="lg" onClick={handleSave} disabled={!isValid}>
                  <FormattedMessage {...protocolDetailModalMessages.save} />
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }
);

ProtocolDetailModal.displayName = 'ProtocolDetailModal';

export default ProtocolDetailModal;
